import { createFileRoute } from '@tanstack/react-router';
import { type CSSProperties, useMemo, useState } from 'react';

import type {
  AmortizeLeases,
  CreateLease,
  LeaseListRow,
  LeaseSchedule as ApiLeaseSchedule,
  LeaseWithSchedule,
  ModifyLease,
  TerminateLease,
} from '@dcc-erp/shared';
import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useAmortizeLeases,
  useCreateLease,
  useLease,
  useLeases,
  useLeaseTaxReconciliation,
  useModificationPreview,
  useModifyLease,
  useTerminateLease,
  useTerminationPreview,
} from '../lib/queries';

type TabKey = 'register' | 'schedule' | 'monthly' | 'tax' | 'disclosure';
type LeaseStatus = 'new' | 'active' | 'expired';
type LeaseTypeCode = 'VEHICLE' | 'LAND' | 'EQUIP';

interface LeaseType {
  code: LeaseTypeCode;
  label: string;
  short: string;
  tone: string;
  acctROU: string;
  acctLiab: string;
}

interface LeaseInput {
  id: string;
  type: LeaseTypeCode;
  lessor: string;
  asset: string;
  start: string;
  end: string;
  months: number;
  pmtMonthly: number;
  rate: number;
  used: number;
  payRef: string;
  dept: string;
}

interface ScheduleRow {
  i: number;
  date: string;
  month: string;
  openBal: number;
  payment: number;
  interest: number;
  principal: number;
  closeBal: number;
  rouDep: number;
  accDep: number;
  rouNbv: number;
}

interface Lease extends LeaseInput {
  schedule: ScheduleRow[];
  rouInitial: number;
  remain: number;
  liabBal: number;
  rouNbv: number;
  currentLiab: number;
  nonCurrentLiab: number;
  ytdInterest: number;
  ytdDep: number;
  status: LeaseStatus;
}

interface MonthlyItem extends Lease {
  interest: number;
  principal: number;
  payment: number;
  dep: number;
}

interface Totals {
  rouNbv: number;
  rouInitial: number;
  liabTotal: number;
  liabCurrent: number;
  liabNonCurrent: number;
  interestYTD: number;
  depYTD: number;
  newCount: number;
  activeCount: number;
}

interface MaturityBucket {
  label: string;
  amt: number;
}

interface TypeAgg {
  code: LeaseTypeCode;
  label: string;
  short: string;
  tone: string;
  acctROU: string;
  acctLiab: string;
  count: number;
  rouInit: number;
  rouNbv: number;
  accDep: number;
  liab: number;
}

function pv(pmt: number, rateAnnual: number, n: number): number {
  const r = rateAnnual / 12;
  if (r === 0) return pmt * n;
  return (pmt * (1 - Math.pow(1 + r, -n))) / r;
}

function buildSchedule(
  pmt: number,
  rateAnnual: number,
  n: number,
  startDate: string,
): { schedule: ScheduleRow[]; rouInitial: number } {
  const r = rateAnnual / 12;
  let bal = pv(pmt, rateAnnual, n);
  const rouInitial = bal;
  const rouDep = rouInitial / n;
  let accDep = 0;
  const out: ScheduleRow[] = [];
  const start = new Date(startDate);
  for (let i = 0; i < n; i++) {
    const interest = bal * r;
    const principal = pmt - interest;
    const openBal = bal;
    bal = bal - principal;
    accDep += rouDep;
    const d = new Date(start.getFullYear(), start.getMonth() + i, 28);
    out.push({
      i: i + 1,
      date: d.toISOString().slice(0, 10),
      month: d.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' }),
      openBal,
      payment: pmt,
      interest,
      principal,
      closeBal: bal,
      rouDep,
      accDep,
      rouNbv: rouInitial - accDep,
    });
  }
  return { schedule: out, rouInitial };
}

const LEASE_TYPES: readonly LeaseType[] = [
  {
    code: 'VEHICLE',
    label: 'รถยานพาหนะ',
    short: 'รถ',
    tone: '#1a56a6',
    acctROU: '10320004',
    acctLiab: '20180003',
  },
  {
    code: 'LAND',
    label: 'ที่ดินและอาคาร',
    short: 'ที่ดิน',
    tone: '#7030A0',
    acctROU: '10320004',
    acctLiab: '20180003',
  },
  {
    code: 'EQUIP',
    label: 'เครื่องจักร/อุปกรณ์',
    short: 'อุปกรณ์',
    tone: '#b54708',
    acctROU: '10320004',
    acctLiab: '20180003',
  },
];

function getLT(c: LeaseTypeCode): LeaseType {
  return LEASE_TYPES.find((t) => t.code === c) ?? LEASE_TYPES[0]!;
}

export const Route = createFileRoute('/lease')({
  component: LeasePage,
});

// ============================================================
// API → prototype-shape adapter.
//
// The prototype's Lease shape is richer than the API list-row (it carries the
// full schedule, NBV, current/non-current split, etc.). The API stores the
// schedule on the server and exposes it via GET /leases/:id. To keep the UI
// untouched we:
//   - synthesize a basic Lease from the LeaseListRow (with an empty schedule)
//   - "hydrate" the selected lease's schedule by issuing useLease() and
//     recomputing the same derived fields the mock pipeline produces.
// Both paths are decimal-safe via the API (server uses decimal.js) — the
// frontend just reads numbers and renders them.
// ============================================================

function num(d: unknown): number {
  if (typeof d === 'number') return d;
  if (typeof d === 'string') {
    const v = parseFloat(d);
    return Number.isFinite(v) ? v : 0;
  }
  return 0;
}

function leaseTypeFromDescription(desc: string): LeaseTypeCode {
  // Heuristic: prototype LEASE_TYPES split. Server stores a free-text
  // description; we infer the COA bucket for tone/badge rendering. Keeps the
  // 3-way visual encoding alive until the API surfaces a real type column.
  const lower = desc.toLowerCase();
  if (lower.includes('ที่ดิน') || lower.includes('โกดัง') || lower.includes('โชว์รูม'))
    return 'LAND';
  if (lower.includes('เครื่อง') || lower.includes('cnc') || lower.includes('อุปกรณ์'))
    return 'EQUIP';
  return 'VEHICLE';
}

// Map of voucherNo -> DB uuid so the schedule fetcher knows which row to GET.
// Keyed off the API list response and rebuilt every render — cheap (< 100 leases
// per company in practice) and saves us extending the prototype Lease type.
function buildIdMap(rows: readonly LeaseListRow[]): Record<string, string> {
  const m: Record<string, string> = {};
  rows.forEach((r) => {
    m[r.voucherNo] = r.id;
  });
  return m;
}

function adaptListRowToLease(row: LeaseListRow): Lease {
  const pmt = num(row.paymentAmount);
  const rate = num(row.discountRate) / 100; // API stores percent; prototype uses fraction
  const months = row.months;
  const used = row.monthsElapsed;
  const rouInitial = num(row.presentValue);
  const liabBal = num(row.leaseLiability);
  const rouNbv = num(row.rouNbv);
  return {
    id: row.voucherNo,
    type: leaseTypeFromDescription(row.description),
    lessor: row.lessorDisplayName,
    asset: row.description,
    start:
      typeof row.startDate === 'string'
        ? row.startDate
        : new Date(row.startDate).toISOString().slice(0, 10),
    end:
      typeof row.endDate === 'string'
        ? row.endDate
        : new Date(row.endDate).toISOString().slice(0, 10),
    months,
    pmtMonthly: pmt,
    rate,
    used,
    payRef: '',
    dept: '',
    schedule: [], // hydrated on selection via useLease()
    rouInitial,
    remain: Math.max(0, months - used),
    liabBal,
    rouNbv,
    currentLiab: 0,
    nonCurrentLiab: liabBal,
    ytdInterest: 0,
    ytdDep: rouInitial - rouNbv,
    status: row.status === 'EXPIRED' ? 'expired' : used === 0 ? 'new' : 'active',
  };
}

function adaptScheduleRow(
  api: ApiLeaseSchedule,
  idx: number,
  rouNbvStart: number,
  depPerMonth: number,
): ScheduleRow {
  const date = `${api.period.slice(0, 4)}-${api.period.slice(4, 6)}-28`;
  const m = new Date(Number(api.period.slice(0, 4)), Number(api.period.slice(4, 6)) - 1, 1);
  return {
    i: idx + 1,
    date,
    month: m.toLocaleDateString('th-TH', { month: 'short', year: '2-digit' }),
    openBal: num(api.openingLiability),
    payment: num(api.payment),
    interest: num(api.interestExpense),
    principal: num(api.principalReduction),
    closeBal: num(api.closingLiability),
    rouDep: num(api.rouDepreciation),
    accDep: depPerMonth * (idx + 1),
    rouNbv: rouNbvStart - depPerMonth * (idx + 1),
  };
}

// Shown on the schedule/tax tabs when there are no leases to detail yet.
function LeaseEmpty() {
  return (
    <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
      ยังไม่มีสัญญาเช่า — กด “เพิ่มสัญญาเช่า” ในแท็บทะเบียนเพื่อเริ่มต้น
    </div>
  );
}

function LeasePage() {
  const [tab, setTab] = useState<TabKey>('register');
  const [q, setQ] = useState<string>('');
  const [typeF, setTypeF] = useState<string>('all');
  const [selId, setSelId] = useState<string | undefined>(undefined);
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [showTerminate, setShowTerminate] = useState<boolean>(false);
  const [showModify, setShowModify] = useState<boolean>(false);

  const companyId = useActiveCompanyId();
  const leasesQ = useLeases(companyId);
  const createLease = useCreateLease(companyId);
  const amortize = useAmortizeLeases(companyId);
  const terminate = useTerminateLease(companyId);
  const modify = useModifyLease(companyId);

  // Map UI selection (voucherNo) → DB uuid for the detail GET.
  const idMap = useMemo<Record<string, string>>(
    () => buildIdMap(leasesQ.data ?? []),
    [leasesQ.data],
  );
  // Pull the full lease (with schedule) for the currently selected row.
  // Falls back to the first API row when nothing is explicitly selected.
  const fallbackVoucherNo = leasesQ.data?.[0]?.voucherNo;
  const detailVoucherNo = selId ?? fallbackVoucherNo;
  const detailUuid = detailVoucherNo ? idMap[detailVoucherNo] : undefined;
  const detailQ = useLease(companyId, detailUuid);

  // Adapt API list to prototype Lease[]. No mock fallback — zero rows renders a
  // real empty-state instead of fabricated sample leases.
  const liveLeases = useMemo<Lease[]>(
    () => (leasesQ.data ?? []).map(adaptListRowToLease),
    [leasesQ.data],
  );

  // When the detail query lands, fold the schedule + derived fields into the
  // matching list-row entry so Tab 2 renders real data.
  const dataLeases = useMemo<Lease[]>(() => {
    if (!detailQ.data) return liveLeases;
    const d: LeaseWithSchedule = detailQ.data;
    return liveLeases.map((l) => {
      if (l.id !== d.voucherNo) return l;
      const pv = num(d.presentValue);
      const depPerMonth = pv / d.months;
      const schedule = d.schedules.map((s, idx) => adaptScheduleRow(s, idx, pv, depPerMonth));
      const used = d.schedules.filter((s) => s.status === 'POSTED').length;
      const remain = Math.max(0, d.months - used);
      const currentLiab = schedule
        .slice(used, used + Math.min(12, remain))
        .reduce((s, r) => s + r.principal, 0);
      const ytdInterest = schedule.slice(0, used).reduce((s, r) => s + r.interest, 0);
      return {
        ...l,
        schedule,
        rouInitial: pv,
        currentLiab,
        nonCurrentLiab: Math.max(0, l.liabBal - currentLiab),
        ytdInterest,
      };
    });
  }, [liveLeases, detailQ.data]);

  const sel = useMemo<Lease | null>(() => {
    if (selId) return dataLeases.find((l) => l.id === selId) ?? dataLeases[0] ?? null;
    return dataLeases[0] ?? null;
  }, [dataLeases, selId]);

  const filtered = useMemo<Lease[]>(
    () =>
      dataLeases.filter((r) => {
        if (typeF !== 'all' && r.type !== typeF) return false;
        if (
          q &&
          !(
            r.id.toLowerCase().includes(q.toLowerCase()) ||
            r.lessor.toLowerCase().includes(q.toLowerCase()) ||
            r.asset.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [q, typeF, dataLeases],
  );

  const totals = useMemo<Totals>(() => {
    const active = dataLeases.filter((l) => l.status === 'active');
    return {
      rouNbv: active.reduce((s, l) => s + l.rouNbv, 0),
      rouInitial: active.reduce((s, l) => s + l.rouInitial, 0),
      liabTotal: active.reduce((s, l) => s + l.liabBal, 0),
      liabCurrent: active.reduce((s, l) => s + l.currentLiab, 0),
      liabNonCurrent: active.reduce((s, l) => s + l.nonCurrentLiab, 0),
      interestYTD: active.reduce((s, l) => s + l.ytdInterest, 0),
      depYTD: active.reduce((s, l) => s + l.ytdDep, 0),
      newCount: dataLeases.filter((l) => l.status === 'new').length,
      activeCount: active.length,
    };
  }, [dataLeases]);

  const thisMonthRun = useMemo<MonthlyItem[]>(
    () =>
      dataLeases
        .filter((l) => l.status === 'active' && l.schedule.length > 0)
        .map((l) => {
          const cur = l.schedule[l.used] ?? l.schedule[l.used - 1]!;
          return {
            ...l,
            interest: cur.interest,
            principal: cur.principal,
            payment: cur.payment,
            dep: cur.rouDep,
          };
        }),
    [dataLeases],
  );

  // ============================================================
  // Mutation wrappers used by sub-components / modal.
  // ============================================================
  const handleAmortize = (period: string) => {
    if (!companyId) return;
    const data: AmortizeLeases = { period };
    amortize.mutate({ data });
  };

  const handleCreate = (data: CreateLease) => {
    if (!companyId) return;
    createLease.mutate(
      { data },
      {
        onSuccess: () => {
          setShowAdd(false);
        },
      },
    );
  };

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', 'ทะเบียนสัญญาเช่า'],
    ['schedule', 'ตารางตัดจ่ายสัญญา'],
    ['monthly', 'ประมวลผลรายเดือน'],
    ['tax', 'เปรียบเทียบบัญชี vs ภาษี'],
    ['disclosure', 'หมายเหตุประกอบงบ (Disclosure)'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'สัญญาเช่า TFRS 16']}
        title="สัญญาเช่ารถ + สัญญาเช่าที่ดิน — ตามมาตรฐาน TFRS 16"
        actions={
          <>
            <HelpButton guide="lease" />
            <select className="select" defaultValue="2026">
              <option value="2026">ปี: 2026</option>
              <option value="2025">ปี: 2025</option>
            </select>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="download" size={14} /> Export Disclosure
            </button>
            <button className="btn primary" onClick={() => setShowAdd(true)}>
              <Icon name="plus" size={14} /> ลงทะเบียนสัญญาใหม่
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            สินทรัพย์สิทธิการใช้ (ROU) — สุทธิ
          </div>
          <div className="value tnum">
            {fmtB(totals.rouNbv)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            {totals.activeCount} สัญญา · ทุนเริ่มต้น {fmtB(totals.rouInitial)} บาท
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            หนี้สินตามสัญญาเช่า
          </div>
          <div className="value tnum">
            {fmtB(totals.liabTotal)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            หมุนเวียน {fmtB(totals.liabCurrent)} · ไม่หมุนเวียน {fmtB(totals.liabNonCurrent)}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ค่าเสื่อมราคา ROU (YTD)
          </div>
          <div className="value tnum">
            {fmtB(totals.depYTD)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">วิธีเส้นตรง · บันทึกใน P&L</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            ดอกเบี้ยจ่าย — สัญญาเช่า (YTD)
          </div>
          <div className="value tnum">
            {fmtB(totals.interestYTD)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">วิธี Effective Interest · IBR เฉลี่ย 5.4%</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการบันทึกบัญชีตาม TFRS 16
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, fontSize: 14 }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>เริ่มสัญญา</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. ROU Asset (10320004)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. Lease Liability (20180003)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              มูลค่า = PV ของค่าเช่าทั้งหมด
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>ดอกเบี้ยทุกเดือน</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. Interest Expense (60200013)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. Lease Liability (20180003)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              ดอกเบี้ย = ยอดคงเหลือ × IBR/12
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>จ่ายค่าเช่า</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. Lease Liability (20180003)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. เงินฝากธนาคาร (บัญชีหลักของบริษัท)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              เงินจ่ายลดยอดหนี้สิน
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>ค่าเสื่อมราคา ROU</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. Depreciation (60110111)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. Acc. Dep ROU (10280020)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              เส้นตรงตลอดอายุสัญญา
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <LeaseList
          rows={filtered}
          allRows={dataLeases}
          q={q}
          setQ={setQ}
          typeF={typeF}
          setTypeF={setTypeF}
          onPick={(l) => {
            setSelId(l.id);
            setTab('schedule');
          }}
        />
      )}
      {tab === 'schedule' &&
        (sel ? (
          <LeaseSchedule
            lease={sel}
            allLeases={dataLeases}
            setLease={(l) => setSelId(l.id)}
            onTerminate={() => setShowTerminate(true)}
            onModify={() => setShowModify(true)}
          />
        ) : (
          <LeaseEmpty />
        ))}
      {tab === 'monthly' && (
        <LeaseMonthly
          items={thisMonthRun}
          onAmortize={handleAmortize}
          isPosting={amortize.isPending}
        />
      )}
      {tab === 'tax' && (sel ? <LeaseTaxComparison lease={sel} /> : <LeaseEmpty />)}
      {tab === 'disclosure' && <LeaseDisclosure allLeases={dataLeases} />}

      {showAdd && (
        <LeaseAddModal
          companyId={companyId}
          onClose={() => setShowAdd(false)}
          onSave={handleCreate}
          isSaving={createLease.isPending}
        />
      )}

      {showTerminate && detailUuid && sel && (
        <TerminateLeaseModal
          companyId={companyId}
          leaseUuid={detailUuid}
          lease={sel}
          onClose={() => setShowTerminate(false)}
          onConfirm={(data) =>
            terminate.mutate({ id: detailUuid, data }, { onSuccess: () => setShowTerminate(false) })
          }
          isPosting={terminate.isPending}
          postError={terminate.error instanceof Error ? terminate.error.message : null}
        />
      )}

      {showModify && detailUuid && sel && (
        <ModifyLeaseModal
          companyId={companyId}
          leaseUuid={detailUuid}
          lease={sel}
          onClose={() => setShowModify(false)}
          onConfirm={(data) =>
            modify.mutate({ id: detailUuid, data }, { onSuccess: () => setShowModify(false) })
          }
          isPosting={modify.isPending}
          postError={modify.error instanceof Error ? modify.error.message : null}
        />
      )}
    </div>
  );
}

interface LeaseListProps {
  rows: readonly Lease[];
  allRows: readonly Lease[];
  q: string;
  setQ: (s: string) => void;
  typeF: string;
  setTypeF: (s: string) => void;
  onPick: (l: Lease) => void;
}

function LeaseList({ rows, allRows, q, setQ, typeF, setTypeF, onPick }: LeaseListProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่สัญญา / ผู้ให้เช่า / สินทรัพย์"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={typeF} onChange={(e) => setTypeF(e.target.value)}>
          <option value="all">ประเภทสัญญา: ทั้งหมด</option>
          {LEASE_TYPES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
        <select className="select">
          <option>หน่วยงาน: ทั้งหมด</option>
          <option>ขนส่ง</option>
          <option>ขาย</option>
          <option>บริหาร</option>
        </select>
        <select className="select">
          <option>สถานะ: กำลังมีผล</option>
          <option>เริ่มต้น</option>
          <option>สิ้นสุดแล้ว</option>
          <option>ยกเลิก</option>
        </select>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 540 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th style={{ width: 130 }}>เลขที่สัญญา</th>
              <th>ผู้ให้เช่า / สินทรัพย์</th>
              <th>ประเภท</th>
              <th>วันเริ่มสัญญา</th>
              <th>วันสิ้นสุด</th>
              <th className="num">งวด</th>
              <th className="num">ค่าเช่า/เดือน</th>
              <th className="num">IBR</th>
              <th className="num">ROU เริ่มต้น</th>
              <th className="num">ROU คงเหลือ</th>
              <th className="num">หนี้สินคงเหลือ</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={13} className="muted" style={{ padding: 28, textAlign: 'center' }}>
                  ยังไม่มีสัญญาเช่า — กด “เพิ่มสัญญาเช่า” เพื่อเริ่มต้น
                </td>
              </tr>
            )}
            {rows.map((l) => {
              const t = getLT(l.type);
              const pct = (l.used / l.months) * 100;
              return (
                <tr key={l.id} onClick={() => onPick(l)} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{l.id}</a>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{l.asset}</div>
                    <div className="meta" style={{ marginTop: 1 }}>
                      {l.lessor} · {l.dept}
                    </div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: t.tone }} />
                      {t.short}
                    </span>
                  </td>
                  <td>{fmtDate(l.start)}</td>
                  <td>{fmtDate(l.end)}</td>
                  <td className="num">
                    <div>
                      {l.used}/{l.months}
                    </div>
                    <div
                      style={{
                        height: 4,
                        borderRadius: 2,
                        background: '#f0f0f3',
                        overflow: 'hidden',
                        marginTop: 3,
                      }}
                    >
                      <div style={{ width: `${pct}%`, height: '100%', background: t.tone }} />
                    </div>
                  </td>
                  <td className="num tnum">{fmt(l.pmtMonthly)}</td>
                  <td className="num">{(l.rate * 100).toFixed(2)}%</td>
                  <td className="num tnum">{fmt(l.rouInitial, 0)}</td>
                  <td className="num tnum">
                    <b>{fmt(l.rouNbv, 0)}</b>
                  </td>
                  <td className="num tnum">
                    <b>{fmt(l.liabBal, 0)}</b>
                  </td>
                  <td>
                    {l.status === 'new' ? (
                      <span className="badge info">
                        <span className="bdot" />
                        เริ่มต้น
                      </span>
                    ) : l.status === 'expired' ? (
                      <span className="badge neutral">
                        <span className="bdot" />
                        สิ้นสุด
                      </span>
                    ) : (
                      <span className="badge positive">
                        <span className="bdot" />
                        กำลังมีผล
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={7}>รวม {rows.length} สัญญา</td>
              <td className="num tnum">{fmt(rows.reduce((s, l) => s + l.pmtMonthly, 0))}</td>
              <td />
              <td className="num tnum">
                {fmt(
                  rows.reduce((s, l) => s + l.rouInitial, 0),
                  0,
                )}
              </td>
              <td className="num tnum">
                {fmt(
                  rows.reduce((s, l) => s + l.rouNbv, 0),
                  0,
                )}
              </td>
              <td className="num tnum">
                {fmt(
                  rows.reduce((s, l) => s + l.liabBal, 0),
                  0,
                )}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>
          แสดง {rows.length} จาก {allRows.length} สัญญา · ROU = 10320004 · Lease Liability =
          20180003 · คลิกแถวเพื่อดูตารางคำนวณ
        </span>
      </div>
    </div>
  );
}

interface LeaseScheduleProps {
  lease: Lease;
  allLeases: readonly Lease[];
  setLease: (l: Lease) => void;
  onTerminate: () => void;
  onModify: () => void;
}

function LeaseSchedule({ lease, allLeases, setLease, onTerminate, onModify }: LeaseScheduleProps) {
  const t = getLT(lease.type);
  const [yearFilter, setYearFilter] = useState<string>('all');

  const filteredSchedule = lease.schedule.filter((s) => {
    if (yearFilter === 'all') return true;
    return s.date.startsWith(yearFilter);
  });

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select
              className="select"
              style={{ width: 380, height: 36, fontSize: 14 }}
              value={lease.id}
              onChange={(e) => {
                const next = allLeases.find((l) => l.id === e.target.value);
                if (next) setLease(next);
              }}
            >
              {allLeases.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.id} — {l.asset.slice(0, 40)}
                </option>
              ))}
            </select>
            <span
              className="badge"
              style={{ background: t.tone + '20', color: t.tone, fontWeight: 600 }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: t.tone,
                  marginRight: 4,
                }}
              />{' '}
              {t.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="print" size={12} /> พิมพ์ตาราง
            </button>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              <Icon name="download" size={12} /> Excel
            </button>
            <button
              className="btn sm"
              onClick={onModify}
              disabled={lease.status !== 'active'}
              title={
                lease.status !== 'active'
                  ? 'ปรับปรุงได้เฉพาะสัญญาที่กำลังมีผล'
                  : 'เปลี่ยนแปลงค่าเช่าระหว่างอายุสัญญา'
              }
            >
              ปรับปรุงสัญญา
            </button>
            <button
              className="btn sm"
              onClick={onTerminate}
              disabled={lease.status !== 'active'}
              title={
                lease.status !== 'active'
                  ? 'ยกเลิกได้เฉพาะสัญญาที่กำลังมีผล'
                  : 'ยกเลิกสัญญาก่อนกำหนด'
              }
              style={{ color: 'var(--negative, #b42318)', borderColor: 'var(--negative, #b42318)' }}
            >
              <Icon name="x" size={12} /> ยกเลิกสัญญา
            </button>
          </div>
        </div>
        <div style={{ padding: '18px' }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{lease.asset}</div>
          <div className="meta">
            ผู้ให้เช่า: <b style={{ color: '#3f3f46' }}>{lease.lessor}</b> · หน่วยงาน:{' '}
            <b style={{ color: '#3f3f46' }}>{lease.dept}</b> · อ้างอิงการจ่ายครั้งแรก:{' '}
            {lease.payRef}
          </div>

          <div
            style={{
              marginTop: 16,
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: 16,
            }}
          >
            <div>
              <div className="meta">อายุสัญญา</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {lease.months} เดือน
              </div>
              <div className="meta">
                {fmtDate(lease.start)} → {fmtDate(lease.end)}
              </div>
            </div>
            <div>
              <div className="meta">ค่าเช่า/เดือน (PMT)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(lease.pmtMonthly)}
              </div>
              <div className="meta">รวมจ่าย {fmtB(lease.pmtMonthly * lease.months)} บาท</div>
            </div>
            <div>
              <div className="meta">IBR (อัตราดอกเบี้ย)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--info)',
                  fontFamily: 'var(--font-display)',
                }}
              >
                {(lease.rate * 100).toFixed(2)}%
              </div>
              <div className="meta">ต่อปี · Effective</div>
            </div>
            <div>
              <div className="meta">ROU เริ่มต้น (PV)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(lease.rouInitial, 0)}
              </div>
              <div className="meta">บัญชี {t.acctROU}</div>
            </div>
            <div>
              <div className="meta">ROU คงเหลือ (NBV)</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--positive)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(lease.rouNbv, 0)}
              </div>
              <div className="meta">
                ตัดไป {lease.used}/{lease.months} งวด
              </div>
            </div>
            <div>
              <div className="meta">หนี้สินคงเหลือ</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--negative)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(lease.liabBal, 0)}
              </div>
              <div className="meta">บัญชี {t.acctLiab}</div>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              background: '#fafafa',
              borderRadius: 8,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <div className="meta">การแยกหนี้สินในงบดุล:</div>
            <div style={{ display: 'flex', gap: 24 }}>
              <div>
                <span className="meta">หนี้สินหมุนเวียน (≤ 12 เดือน):</span>
                <b className="tnum" style={{ marginLeft: 6 }}>
                  {fmt(lease.currentLiab, 0)}
                </b>
              </div>
              <div>
                <span className="meta">หนี้สินไม่หมุนเวียน:</span>
                <b className="tnum" style={{ marginLeft: 6 }}>
                  {fmt(lease.nonCurrentLiab, 0)}
                </b>
              </div>
              <div>
                <span className="meta">รวม:</span>
                <b className="tnum" style={{ marginLeft: 6, color: 'var(--brand)' }}>
                  {fmt(lease.liabBal, 0)}
                </b>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            ตารางคำนวณดอกเบี้ย + เงินต้น + ค่าเสื่อมราคา ROU
          </span>
          <select
            className="select"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value="all">ทั้งหมด ({lease.months} งวด)</option>
            <option value="2024">ปี 2024</option>
            <option value="2025">ปี 2025</option>
            <option value="2026">ปี 2026</option>
            <option value="2027">ปี 2027</option>
            <option value="2028">ปี 2028</option>
            <option value="2029">ปี 2029</option>
            <option value="2030">ปี 2030</option>
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <span className="chip">
              <span
                style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--positive)' }}
              />{' '}
              Posted แล้ว ({lease.used})
            </span>
            <span className="chip">
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--border-strong)',
                }}
              />{' '}
              Forecast ({lease.months - lease.used})
            </span>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>งวด</th>
                <th style={{ width: 110 }}>เดือน</th>
                <th className="num">ยอดหนี้สินยกมา</th>
                <th className="num">ค่าเช่าจ่าย</th>
                <th className="num">ดอกเบี้ย</th>
                <th className="num">เงินต้น</th>
                <th className="num">ยอดหนี้สินยกไป</th>
                <th className="num">ค่าเสื่อม ROU</th>
                <th className="num">ROU คงเหลือ</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchedule.map((s) => {
                const isPosted = s.i <= lease.used;
                const isCurrent = s.i === lease.used + 1;
                return (
                  <tr key={s.i} style={isCurrent ? { background: 'var(--brand-tint)' } : undefined}>
                    <td className="num">{s.i}</td>
                    <td>{s.month}</td>
                    <td className="num tnum muted">{fmt(s.openBal, 0)}</td>
                    <td className="num tnum">{fmt(s.payment, 0)}</td>
                    <td className="num tnum" style={{ color: 'var(--info)' }}>
                      {fmt(s.interest, 0)}
                    </td>
                    <td className="num tnum">{fmt(s.principal, 0)}</td>
                    <td className="num tnum">{fmt(s.closeBal, 0)}</td>
                    <td className="num tnum" style={{ color: 'var(--warning)' }}>
                      {fmt(s.rouDep, 0)}
                    </td>
                    <td className="num tnum">{fmt(s.rouNbv, 0)}</td>
                    <td>
                      {isPosted ? (
                        <span className="badge positive">
                          <span className="bdot" />
                          Posted
                        </span>
                      ) : isCurrent ? (
                        <span className="badge brand">
                          <span className="bdot" />
                          งวดปัจจุบัน
                        </span>
                      ) : (
                        <span className="badge neutral">
                          <span className="bdot" />
                          Forecast
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td colSpan={3}>รวมตลอดอายุสัญญา</td>
                <td className="num tnum">{fmt(lease.pmtMonthly * lease.months, 0)}</td>
                <td className="num tnum">
                  {fmt(lease.pmtMonthly * lease.months - lease.rouInitial, 0)}
                </td>
                <td className="num tnum">{fmt(lease.rouInitial, 0)}</td>
                <td colSpan={2} />
                <td className="num tnum">{fmt(lease.rouInitial, 0)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

interface LeaseMonthlyProps {
  items: readonly MonthlyItem[];
  onAmortize: (period: string) => void;
  isPosting: boolean;
}

function LeaseMonthly({ items, onAmortize, isPosting }: LeaseMonthlyProps) {
  const totalInterest = items.reduce((s, i) => s + i.interest, 0);
  const totalPrincipal = items.reduce((s, i) => s + i.principal, 0);
  const totalPayment = items.reduce((s, i) => s + i.payment, 0);
  const totalDep = items.reduce((s, i) => s + i.dep, 0);

  // Period for the amortize button — use the first item's next-pending period
  // when available, otherwise default to the current calendar month. The
  // prototype labels this "พ.ค. 2026"; the live wiring uses the actual
  // current period so demo + prod behave the same.
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div>
            <h3>ประมวลผลรายเดือน — เดือน พ.ค. 2026</h3>
            <div className="sub">
              รัน 3 batch: ดอกเบี้ย + จ่ายค่าเช่า + ค่าเสื่อมราคา ROU — ทุกสิ้นเดือนอัตโนมัติ
            </div>
          </div>
          <button
            className="btn primary"
            disabled={isPosting}
            onClick={() => onAmortize(defaultPeriod)}
          >
            <Icon name="check" size={14} /> {isPosting ? 'กำลังประมวลผล...' : 'ประมวลผล & ลง JV'}
          </button>
        </div>
        <div
          style={{
            padding: '18px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 18,
          }}
        >
          <div>
            <div className="meta">ดอกเบี้ยจ่ายเดือนนี้</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--info)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalInterest, 0)}
            </div>
            <div className="meta">Dr.60200013 / Cr.Lease Liab</div>
          </div>
          <div>
            <div className="meta">จ่ายค่าเช่า</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalPayment, 0)}
            </div>
            <div className="meta">Dr.Lease Liab / Cr.ธนาคาร</div>
          </div>
          <div>
            <div className="meta">เงินต้นที่ลดลง</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalPrincipal, 0)}
            </div>
            <div className="meta">= ค่าเช่า − ดอกเบี้ย</div>
          </div>
          <div>
            <div className="meta">ค่าเสื่อมราคา ROU</div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                marginTop: 4,
                color: 'var(--warning)',
                fontFamily: 'var(--font-display)',
              }}
              className="tnum"
            >
              {fmt(totalDep, 0)}
            </div>
            <div className="meta">Dr.60110111 / Cr.10280020</div>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            รายการที่จะลงบัญชี — {items.length} สัญญา
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              ดู JV ก่อน
            </button>
            <button
              className="btn sm"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              Export Preview
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 540 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 24 }}>
                  <input type="checkbox" defaultChecked />
                </th>
                <th style={{ width: 130 }}>เลขที่สัญญา</th>
                <th>สินทรัพย์</th>
                <th>ประเภท</th>
                <th className="num">งวดที่</th>
                <th className="num">ดอกเบี้ย</th>
                <th className="num">เงินต้น</th>
                <th className="num">รวมจ่ายเงินสด</th>
                <th className="num">ค่าเสื่อม ROU</th>
                <th className="num">รวมผลกระทบ P&L</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const t = getLT(l.type);
                return (
                  <tr key={l.id}>
                    <td>
                      <input type="checkbox" defaultChecked />
                    </td>
                    <td className="code">{l.id}</td>
                    <td>
                      <div>{l.asset.slice(0, 48)}</div>
                      <div className="meta">{l.lessor}</div>
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{ width: 8, height: 8, borderRadius: 2, background: t.tone }}
                        />
                        {t.short}
                      </span>
                    </td>
                    <td className="num">
                      {l.used + 1}/{l.months}
                    </td>
                    <td className="num tnum" style={{ color: 'var(--info)' }}>
                      {fmt(l.interest, 0)}
                    </td>
                    <td className="num tnum">{fmt(l.principal, 0)}</td>
                    <td className="num tnum">{fmt(l.payment, 0)}</td>
                    <td className="num tnum" style={{ color: 'var(--warning)' }}>
                      {fmt(l.dep, 0)}
                    </td>
                    <td className="num tnum">
                      <b>{fmt(l.interest + l.dep, 0)}</b>
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td colSpan={5}>รวมทั้งสิ้น</td>
                <td className="num tnum">{fmt(totalInterest, 0)}</td>
                <td className="num tnum">{fmt(totalPrincipal, 0)}</td>
                <td className="num tnum">{fmt(totalPayment, 0)}</td>
                <td className="num tnum">{fmt(totalDep, 0)}</td>
                <td className="num tnum">{fmt(totalInterest + totalDep, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid-2-eq" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>JV-2026-05-LSE13 — ดอกเบี้ย + ค่าเช่า</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>บัญชี</th>
                  <th>ชื่อ</th>
                  <th className="num">เดบิต</th>
                  <th className="num">เครดิต</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="code">60200013</td>
                  <td>ดอกเบี้ยจ่าย — สัญญาเช่า</td>
                  <td className="num tnum">{fmt(totalInterest, 0)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">20180003</td>
                  <td>หนี้สินตามสัญญาเช่า (จ่ายเงินต้น)</td>
                  <td className="num tnum">{fmt(totalPrincipal, 0)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">เงินฝากธนาคาร</td>
                  <td>เงินฝากธนาคาร (บัญชีหลักของบริษัท) — จ่ายค่าเช่า</td>
                  <td className="num">—</td>
                  <td className="num tnum">{fmt(totalPayment, 0)}</td>
                </tr>
                <tr className="total">
                  <td colSpan={2}>รวม</td>
                  <td className="num tnum">{fmt(totalPayment, 0)}</td>
                  <td className="num tnum">{fmt(totalPayment, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h3>JV-2026-05-LSE14 — ค่าเสื่อม ROU</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>บัญชี</th>
                  <th>ชื่อ</th>
                  <th className="num">เดบิต</th>
                  <th className="num">เครดิต</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="code">60110111</td>
                  <td>ค่าเสื่อมราคา — ROU Asset</td>
                  <td className="num tnum">{fmt(totalDep, 0)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">10280020</td>
                  <td>ค่าเสื่อมราคาสะสม — ROU</td>
                  <td className="num">—</td>
                  <td className="num tnum">{fmt(totalDep, 0)}</td>
                </tr>
                <tr className="total">
                  <td colSpan={2}>รวม</td>
                  <td className="num tnum">{fmt(totalDep, 0)}</td>
                  <td className="num tnum">{fmt(totalDep, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// LeaseTaxComparison — Phase 14b/c. Renders the per-fiscal-year book vs tax
// reconciliation alongside lifetime totals + max DTA balance. Pulls the API
// `getTaxReconciliation` shape; falls back to "no data" when no lease is
// selected (e.g., page loaded before any leases are picked).
// ============================================================

interface LeaseTaxComparisonProps {
  lease: Lease | undefined;
}

function LeaseTaxComparison({ lease }: LeaseTaxComparisonProps) {
  const companyId = useActiveCompanyId();
  // Local seeded leases use 'L-xxxx' codes; only API-backed rows have real
  // UUIDs that the endpoint accepts. Skip the query when the selected lease
  // is a seeded placeholder.
  const isApiLease = typeof lease?.id === 'string' && lease.id.length === 36;
  const { data, isPending } = useLeaseTaxReconciliation(
    companyId,
    isApiLease ? lease.id : undefined,
  );

  if (!lease) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 24, textAlign: 'center', color: '#666' }}>
          เลือกสัญญาเช่าจากแท็บทะเบียนก่อน
        </div>
      </div>
    );
  }

  if (!isApiLease) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 24, textAlign: 'center', color: '#666' }}>
          สัญญานี้เป็น seed data — กรุณาเลือกสัญญาที่บันทึกผ่าน API
        </div>
      </div>
    );
  }

  if (isPending || !data) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 24, textAlign: 'center', color: '#666' }}>
          กำลังคำนวณ…
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Lifetime + key metrics */}
      <div className="kpi-grid" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            รวมค่าใช้จ่ายบัญชี (book) ตลอดสัญญา
          </div>
          <div className="value tnum">{fmt(Number(data.lifetime.bookExpense), 0)}</div>
          <div className="trend faint">ดอกเบี้ย + ค่าเสื่อม ROU · TFRS 16 front-loaded</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            รวมค่าใช้จ่ายภาษี (tax) ตลอดสัญญา
          </div>
          <div className="value tnum">{fmt(Number(data.lifetime.taxExpense), 0)}</div>
          <div className="trend faint">ค่าเช่าจริงที่จ่าย (ตาม Section 65)</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            DTA สูงสุดระหว่างอายุสัญญา
          </div>
          <div className="value tnum">{fmt(Number(data.lifetime.peakDtaBalance), 0)}</div>
          <div className="trend faint">
            {data.lifetime.peakDtaYear ? `ปี ${data.lifetime.peakDtaYear}` : '—'} · อัตราภาษี{' '}
            {data.taxAnnualRate}%
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: '#888' }} />
            ส่วนต่าง (residual)
          </div>
          <div className="value tnum">{fmt(Number(data.lifetime.residual), 2)}</div>
          <div className="trend faint">≈ 0 ถ้าตรง · เกินจากการปัดเศษ</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h3>เปรียบเทียบรายปี — บัญชี vs ภาษี + DTA</h3>
            <div className="sub">
              {lease.asset} · {data.voucherNo} · {data.years.length} ปีบัญชี
            </div>
          </div>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>ปีบัญชี</th>
                <th className="num">งวด</th>
                <th className="num">ค่าใช้จ่ายบัญชี</th>
                <th className="num">ค่าใช้จ่ายภาษี</th>
                <th className="num">ผลต่าง (book − tax)</th>
                <th className="num">ผลต่างสะสม</th>
                <th className="num">DTA คงเหลือสิ้นปี</th>
              </tr>
            </thead>
            <tbody>
              {data.years.map((y) => {
                const diff = Number(y.diff);
                const dta = Number(y.dtaBalance);
                return (
                  <tr key={y.fiscalYear}>
                    <td className="code">{y.fiscalYear}</td>
                    <td className="num">{y.periodCount}</td>
                    <td className="num tnum">{fmt(Number(y.bookExpense), 0)}</td>
                    <td className="num tnum">{fmt(Number(y.taxExpense), 0)}</td>
                    <td
                      className="num tnum"
                      style={{
                        color: diff > 0 ? 'var(--info)' : diff < 0 ? 'var(--warning)' : '#888',
                      }}
                    >
                      {diff > 0 ? '+' : ''}
                      {fmt(diff, 0)}
                    </td>
                    <td className="num tnum">{fmt(Number(y.cumulativeDiff), 0)}</td>
                    <td
                      className="num tnum"
                      style={{ fontWeight: 600, color: dta > 0 ? 'var(--info)' : '#888' }}
                    >
                      {fmt(dta, 0)}
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td colSpan={2}>รวมตลอดอายุสัญญา</td>
                <td className="num tnum">{fmt(Number(data.lifetime.bookExpense), 0)}</td>
                <td className="num tnum">{fmt(Number(data.lifetime.taxExpense), 0)}</td>
                <td className="num tnum">{fmt(Number(data.lifetime.residual), 2)}</td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          padding: '14px 18px',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 8,
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>หลักการ TFRS 16 vs ภาษีไทย</div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li>
            <b>บัญชี (TFRS 16):</b> รับรู้ค่าเสื่อม ROU แบบ straight-line + ดอกเบี้ย
            effective-interest → ค่าใช้จ่ายในช่วงต้นสัญญาสูง (front-loaded)
          </li>
          <li>
            <b>ภาษี (ม.65 ประมวลรัษฎากร):</b> รับรู้ค่าใช้จ่ายตามค่าเช่าจริงที่จ่ายในแต่ละงวด
          </li>
          <li>
            <b>ผลต่างชั่วคราว:</b> ปีแรกๆ book {'>'} tax (DTA สะสม), ปีหลัง book {'<'} tax (DTA
            กลับด้าน). สิ้นสัญญารวมแล้วเท่ากัน
          </li>
          <li>
            <b>DTA = ผลต่างสะสม × อัตราภาษี ({data.taxAnnualRate}%)</b> — แสดงเป็น display เท่านั้น
            (ไม่ post JV อัตโนมัติ)
          </li>
        </ul>
      </div>
    </>
  );
}

interface LeaseDisclosureProps {
  allLeases: readonly Lease[];
}

function LeaseDisclosure({ allLeases }: LeaseDisclosureProps) {
  const active = allLeases.filter((l) => l.status === 'active');
  const totalRouNbv = active.reduce((s, l) => s + l.rouNbv, 0);
  const totalRouInit = active.reduce((s, l) => s + l.rouInitial, 0);
  const totalAccDep = totalRouInit - totalRouNbv;

  const maturity: MaturityBucket[] = [
    { label: 'ไม่เกิน 1 ปี', amt: 0 },
    { label: '1 - 2 ปี', amt: 0 },
    { label: '2 - 5 ปี', amt: 0 },
    { label: 'เกิน 5 ปี', amt: 0 },
  ];
  active.forEach((l) => {
    l.schedule.slice(l.used).forEach((s, idx) => {
      const yearsFromNow = idx / 12;
      if (yearsFromNow < 1) maturity[0]!.amt += s.payment;
      else if (yearsFromNow < 2) maturity[1]!.amt += s.payment;
      else if (yearsFromNow < 5) maturity[2]!.amt += s.payment;
      else maturity[3]!.amt += s.payment;
    });
  });
  const totalMaturity = maturity.reduce((s, m) => s + m.amt, 0);

  const byType: Record<LeaseTypeCode, TypeAgg> = {} as Record<LeaseTypeCode, TypeAgg>;
  LEASE_TYPES.forEach((t) => {
    const list = active.filter((l) => l.type === t.code);
    byType[t.code] = {
      ...t,
      count: list.length,
      rouInit: list.reduce((s, l) => s + l.rouInitial, 0),
      rouNbv: list.reduce((s, l) => s + l.rouNbv, 0),
      accDep: list.reduce((s, l) => s + (l.rouInitial - l.rouNbv), 0),
      liab: list.reduce((s, l) => s + l.liabBal, 0),
    };
  });

  const currentBy: Record<LeaseTypeCode, number> = { VEHICLE: 0, LAND: 0, EQUIP: 0 };
  const nonCurrentBy: Record<LeaseTypeCode, number> = { VEHICLE: 0, LAND: 0, EQUIP: 0 };
  LEASE_TYPES.forEach((t) => {
    const list = active.filter((l) => l.type === t.code);
    currentBy[t.code] = list.reduce((s, l) => s + l.currentLiab, 0);
    nonCurrentBy[t.code] = list.reduce((s, l) => s + l.nonCurrentLiab, 0);
  });

  const bucketColors = ['#117a3d', '#1a56a6', '#b54708', '#7030A0'] as const;
  const totalLiab = active.reduce((s, l) => s + l.liabBal, 0);
  const ytdInterest = active.reduce(
    (s, l) =>
      s + l.schedule.slice(Math.max(0, l.used - 5), l.used).reduce((a, r) => a + r.interest, 0),
    0,
  );
  const ytdDep = active.reduce((s, l) => s + (l.rouInitial / l.months) * Math.min(5, l.used), 0);

  return (
    <>
      <div className="grid-2-eq">
        <div className="card">
          <div className="card-head">
            <h3>1. สินทรัพย์สิทธิการใช้ (ROU Assets) — แยกตามประเภท</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>ประเภท</th>
                  <th className="num">จำนวนสัญญา</th>
                  <th className="num">ราคาทุน (PV เริ่มต้น)</th>
                  <th className="num">ค่าเสื่อมสะสม</th>
                  <th className="num">มูลค่าสุทธิ (NBV)</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(byType)
                  .filter((t) => t.count > 0)
                  .map((t) => (
                    <tr key={t.code}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                          <span
                            style={{ width: 9, height: 9, borderRadius: 2, background: t.tone }}
                          />
                          {t.label}
                        </span>
                      </td>
                      <td className="num">{t.count}</td>
                      <td className="num tnum">{fmt(t.rouInit, 0)}</td>
                      <td className="num tnum muted">({fmt(t.accDep, 0)})</td>
                      <td className="num tnum">
                        <b>{fmt(t.rouNbv, 0)}</b>
                      </td>
                    </tr>
                  ))}
                <tr className="total">
                  <td>รวม</td>
                  <td className="num">{Object.values(byType).reduce((s, t) => s + t.count, 0)}</td>
                  <td className="num tnum">{fmt(totalRouInit, 0)}</td>
                  <td className="num tnum">({fmt(totalAccDep, 0)})</td>
                  <td className="num tnum">{fmt(totalRouNbv, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>2. หนี้สินตามสัญญาเช่า — แยกระยะสั้น/ยาว</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>รายการ</th>
                  <th className="num">รถ</th>
                  <th className="num">ที่ดิน</th>
                  <th className="num">อุปกรณ์</th>
                  <th className="num">รวม</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>หนี้สินหมุนเวียน (≤ 1 ปี)</td>
                  <td className="num tnum">{fmt(currentBy.VEHICLE, 0)}</td>
                  <td className="num tnum">{fmt(currentBy.LAND, 0)}</td>
                  <td className="num tnum">{fmt(currentBy.EQUIP, 0)}</td>
                  <td className="num tnum">
                    <b>
                      {fmt(
                        Object.values(currentBy).reduce((s, v) => s + v, 0),
                        0,
                      )}
                    </b>
                  </td>
                </tr>
                <tr>
                  <td>หนี้สินไม่หมุนเวียน (เกิน 1 ปี)</td>
                  <td className="num tnum">{fmt(nonCurrentBy.VEHICLE, 0)}</td>
                  <td className="num tnum">{fmt(nonCurrentBy.LAND, 0)}</td>
                  <td className="num tnum">{fmt(nonCurrentBy.EQUIP, 0)}</td>
                  <td className="num tnum">
                    <b>
                      {fmt(
                        Object.values(nonCurrentBy).reduce((s, v) => s + v, 0),
                        0,
                      )}
                    </b>
                  </td>
                </tr>
                <tr className="total">
                  <td>รวมหนี้สินตามสัญญาเช่า</td>
                  <td className="num tnum">{fmt(currentBy.VEHICLE + nonCurrentBy.VEHICLE, 0)}</td>
                  <td className="num tnum">{fmt(currentBy.LAND + nonCurrentBy.LAND, 0)}</td>
                  <td className="num tnum">{fmt(currentBy.EQUIP + nonCurrentBy.EQUIP, 0)}</td>
                  <td className="num tnum">{fmt(totalLiab, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid-2-eq" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>3. การวิเคราะห์การครบกำหนดของหนี้สิน (ก่อนคิดลด)</h3>
            <span className="meta">ยอดค่าเช่าจ่ายตามอายุที่เหลือ</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {maturity.map((m, i) => (
                <div key={i}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                      fontSize: 14,
                    }}
                  >
                    <span>{m.label}</span>
                    <span>
                      <b className="tnum">{fmt(m.amt, 0)}</b> ·{' '}
                      {totalMaturity ? ((m.amt / totalMaturity) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: '#f0f0f3',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${(m.amt / totalMaturity) * 100}%`,
                        height: '100%',
                        background: bucketColors[i] ?? '#1a56a6',
                      }}
                    />
                  </div>
                </div>
              ))}
              <div
                style={{
                  marginTop: 8,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  <b>รวมยอดที่ไม่ได้คิดลด</b>
                </span>
                <span>
                  <b className="tnum">{fmt(totalMaturity, 0)}</b>
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="muted">หัก: ดอกเบี้ยที่เกี่ยวข้อง</span>
                <span className="tnum muted">({fmt(totalMaturity - totalLiab, 0)})</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  <b>มูลค่าปัจจุบันของหนี้สินตามสัญญาเช่า</b>
                </span>
                <span>
                  <b className="tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(totalLiab, 0)}
                  </b>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>4. รายการในกำไรขาดทุน (P&L) — YTD 2026</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <tbody>
                <tr>
                  <td>ดอกเบี้ยจ่าย — สัญญาเช่า</td>
                  <td className="code muted">60200013</td>
                  <td className="num tnum">{fmt(ytdInterest, 0)}</td>
                </tr>
                <tr>
                  <td>ค่าเสื่อมราคา — ROU Asset</td>
                  <td className="code muted">60110111</td>
                  <td className="num tnum">{fmt(ytdDep, 0)}</td>
                </tr>
                <tr>
                  <td>ค่าใช้จ่าย — สัญญาเช่าระยะสั้น (≤12 เดือน)</td>
                  <td className="code muted">5240</td>
                  <td className="num tnum">{fmt(880_000, 0)}</td>
                </tr>
                <tr>
                  <td>ค่าใช้จ่าย — สัญญาเช่ามูลค่าต่ำ (Low value)</td>
                  <td className="code muted">5240</td>
                  <td className="num tnum">{fmt(124_000, 0)}</td>
                </tr>
                <tr className="total">
                  <td colSpan={2}>รวมผลกระทบต่อ P&L (YTD ม.ค. - พ.ค. 2026)</td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(ytdInterest + ytdDep + 1_004_000, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
            <div
              style={{
                padding: '12px 14px',
                background: '#fafafa',
                fontSize: 14,
                color: 'var(--text-muted)',
                lineHeight: 1.55,
              }}
            >
              <b>การเปิดเผยข้อมูลเพิ่มเติม:</b> บริษัทไม่ได้นำสัญญาเช่าที่มีอายุ ≤ 12 เดือน
              และสัญญาเช่าสินทรัพย์มูลค่าต่ำ (low-value asset) มาคำนวณเป็น ROU Asset ตามข้อยกเว้นใน
              TFRS 16
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

interface LeaseAddModalProps {
  companyId: string | undefined;
  onClose: () => void;
  onSave: (data: CreateLease) => void;
  isSaving: boolean;
}

type PaymentFreq = 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'TRIENNIAL' | 'QUINQUENNIAL' | 'LUMP_SUM';
type DayCount = 'ACTUAL_ACTUAL' | 'ACTUAL_365' | 'ACTUAL_360' | 'THIRTY_360';

const PAYMENT_FREQ_OPTIONS: readonly { value: PaymentFreq; label: string; hint: string }[] = [
  { value: 'MONTHLY', label: 'รายเดือน', hint: 'จ่ายทุกเดือน' },
  { value: 'QUARTERLY', label: 'รายไตรมาส', hint: 'จ่ายทุก 3 เดือน' },
  { value: 'YEARLY', label: 'รายปี', hint: 'จ่ายปีละครั้ง' },
  { value: 'TRIENNIAL', label: 'ราย 3 ปี', hint: 'จ่ายทุก 3 ปี' },
  { value: 'QUINQUENNIAL', label: 'ราย 5 ปี', hint: 'จ่ายทุก 5 ปี' },
  { value: 'LUMP_SUM', label: 'จ่ายครั้งเดียว', hint: 'ชำระทั้งหมดต้นสัญญา' },
];

const DAY_COUNT_OPTIONS: readonly { value: DayCount; label: string }[] = [
  { value: 'ACTUAL_ACTUAL', label: 'ACTUAL/ACTUAL (แนะนำ)' },
  { value: 'ACTUAL_365', label: 'ACTUAL/365' },
  { value: 'ACTUAL_360', label: 'ACTUAL/360 (SAP)' },
  { value: 'THIRTY_360', label: '30/360 (banker)' },
];

function LeaseAddModal({ companyId, onClose, onSave, isSaving }: LeaseAddModalProps) {
  const [type, setType] = useState<LeaseTypeCode>('VEHICLE');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [months, setMonths] = useState<number>(60);
  const [pmt, setPmt] = useState<number>(50000);
  const [rate, setRate] = useState<number>(5.5);
  // `code` is auto-generated from type+year+startDate when the user doesn't
  // provide one. Phase-3 modal still offers code as a future text input slot.
  const code = '';
  const [desc, setDesc] = useState<string>('');
  const [lessor, setLessor] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('2026-06-01');

  // Phase 14 — flexible payment schedule + day-count + tax rate.
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentFreq>('MONTHLY');
  const [paymentDayOfMonth, setPaymentDayOfMonth] = useState<number>(1);
  const [paymentMonthOfYear, setPaymentMonthOfYear] = useState<number>(1);
  const [dayCountConvention, setDayCountConvention] = useState<DayCount>('ACTUAL_ACTUAL');
  const [taxAnnualRate, setTaxAnnualRate] = useState<number>(20);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  // Phase 15 — stepped rent. When hasRentSteps is true the form replaces the
  // single paymentAmount with a list of {fromMonth, toMonth, amount} rows.
  // The backend builder validates contiguous coverage of [1, months].
  const [hasRentSteps, setHasRentSteps] = useState<boolean>(false);
  const [rentSteps, setRentSteps] = useState<
    { fromMonth: number; toMonth: number; amount: number }[]
  >([
    { fromMonth: 1, toMonth: 36, amount: 50_000 },
    { fromMonth: 37, toMonth: 72, amount: 70_000 },
    { fromMonth: 73, toMonth: 108, amount: 100_000 },
  ]);

  // Whether the frequency requires choosing a month of year (YEARLY+ cycles).
  const requiresMonthOfYear =
    paymentFrequency === 'YEARLY' ||
    paymentFrequency === 'TRIENNIAL' ||
    paymentFrequency === 'QUINQUENNIAL';

  const rouInit = pv(pmt, rate / 100, months);
  const totalPmt = pmt * months;
  const totalInterest = totalPmt - rouInit;
  const t = getLT(type);

  const { schedule } = buildSchedule(pmt, rate / 100, Math.min(months, 4), '2026-06-01');

  // End date derived from start + months. End-of-month convention: use last
  // day of (startMonth + months - 1) so a 60-month lease starting 2026-06-01
  // ends 2031-05-31.
  const endDate = (() => {
    const d = new Date(startDate);
    const last = new Date(d.getFullYear(), d.getMonth() + months, 0);
    return last.toISOString().slice(0, 10);
  })();

  const handleSubmit = () => {
    // Auto-generate a code if the user didn't supply one — service requires
    // code unique per company. Pattern matches the prototype's display style.
    const autoCode = code.trim() || `LSE-${new Date(startDate).getFullYear()}-${type}`;
    const freqLabel =
      PAYMENT_FREQ_OPTIONS.find((o) => o.value === paymentFrequency)?.label ?? 'รายเดือน';
    const data: CreateLease = {
      code: autoCode,
      description:
        desc.trim() ||
        `${t.label} — ${
          hasRentSteps
            ? `${rentSteps.length}-step rent`
            : `${pmt.toLocaleString()} บาท/${freqLabel}`
        }`,
      lessorName: lessor.trim() || 'ไม่ระบุผู้ให้เช่า',
      startDate,
      endDate,
      months,
      // When stepped, paymentAmount carries the first step's amount as a
      // fallback / display hint. The actual cycle amounts come from rentSteps.
      paymentAmount: hasRentSteps ? (rentSteps[0]?.amount ?? pmt) : pmt,
      paymentFrequency,
      paymentDayOfMonth,
      ...(requiresMonthOfYear ? { paymentMonthOfYear } : {}),
      paymentTermsDescription: `${freqLabel} · วันที่ ${paymentDayOfMonth}${
        requiresMonthOfYear ? ` ของเดือน ${paymentMonthOfYear}` : ''
      }${hasRentSteps ? ` · ${rentSteps.length} ขั้น` : ''}`,
      discountRate: rate,
      dayCountConvention,
      taxAnnualRate,
      ...(branchId ? { branchId } : {}),
      ...(departmentId ? { departmentId } : {}),
      ...(hasRentSteps ? { rentSteps } : {}),
    };
    onSave(data);
  };

  // Step editor helpers — keep mutation pure-functional for predictability.
  const addStep = () => {
    setRentSteps((prev) => {
      const last = prev[prev.length - 1];
      const nextFromMonth = last ? last.toMonth + 1 : 1;
      return [
        ...prev,
        { fromMonth: nextFromMonth, toMonth: Math.max(nextFromMonth, months), amount: 50_000 },
      ];
    });
  };
  const removeStep = (idx: number) => {
    setRentSteps((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateStep = (
    idx: number,
    patch: Partial<{ fromMonth: number; toMonth: number; amount: number }>,
  ) => {
    setRentSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  // Validation for client-side feedback. Mirrors backend rules:
  // - first.fromMonth = 1
  // - contiguous (next.fromMonth = prev.toMonth + 1)
  // - last.toMonth = months
  // - all amounts > 0
  const stepValidation = (() => {
    if (!hasRentSteps) return { ok: true, msg: '' };
    if (rentSteps.length === 0) return { ok: false, msg: 'ต้องมีอย่างน้อย 1 ขั้น' };
    const sorted = [...rentSteps].sort((a, b) => a.fromMonth - b.fromMonth);
    if (sorted[0]!.fromMonth !== 1) return { ok: false, msg: 'ขั้นแรกต้องเริ่มที่เดือน 1' };
    for (let i = 0; i < sorted.length; i++) {
      const cur = sorted[i]!;
      if (cur.toMonth < cur.fromMonth) {
        return { ok: false, msg: `ขั้น #${i + 1}: toMonth < fromMonth` };
      }
      if (cur.amount <= 0) return { ok: false, msg: `ขั้น #${i + 1}: ค่าเช่าต้องมากกว่า 0` };
      if (i > 0 && cur.fromMonth !== sorted[i - 1]!.toMonth + 1) {
        return { ok: false, msg: `ขั้น #${i + 1}: มีช่องว่างหรือซ้อนกับขั้นก่อนหน้า` };
      }
    }
    const lastTo = sorted[sorted.length - 1]!.toMonth;
    if (lastTo !== months) {
      return { ok: false, msg: `ขั้นสุดท้ายต้องสิ้นสุดเดือน ${months} (ตอนนี้ ${lastTo})` };
    }
    return { ok: true, msg: '' };
  })();

  // Impact analysis — undiscounted total cash + simple average + per-step
  // contribution. Used by the impact panel for finance review.
  const stepImpact = (() => {
    if (!hasRentSteps) return null;
    let totalCash = 0;
    const stepRows = rentSteps.map((s) => {
      const cycleCount = Math.max(0, s.toMonth - s.fromMonth + 1);
      const subtotal = cycleCount * s.amount;
      totalCash += subtotal;
      return { ...s, cycleCount, subtotal };
    });
    const avgPerMonth = months > 0 ? totalCash / months : 0;
    return { totalCash, avgPerMonth, stepRows };
  })();

  const placeholder =
    type === 'VEHICLE'
      ? 'เช่น รถบรรทุก Isuzu FRR 5 คัน ทะเบียน xx-xxxx'
      : type === 'LAND'
        ? 'เช่น ที่ดิน 4,400 ตร.ม. สาขาภาคใต้'
        : 'เช่น เครื่อง CNC รุ่น XYZ';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 920,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ลงทะเบียนสัญญาเช่าใหม่ — TFRS 16
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ใหม่ <b style={{ color: '#3f3f46' }}>LSE-2026-000003</b> · ระบบจะคำนวณ PV
              และสร้างตารางตัดจ่ายอัตโนมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              ประเภทสัญญาเช่า
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {LEASE_TYPES.map((tt) => (
                <div
                  key={tt.code}
                  onClick={() => setType(tt.code)}
                  style={{
                    border:
                      type === tt.code ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                    background: type === tt.code ? 'var(--brand-tint)' : 'white',
                    borderRadius: 8,
                    padding: '12px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: tt.tone }} />
                    <span style={{ fontSize: 15, fontWeight: type === tt.code ? 600 : 500 }}>
                      {tt.label}
                    </span>
                  </div>
                  <div className="meta" style={{ fontSize: 13 }}>
                    ROU: {tt.acctROU} · Liab: {tt.acctLiab}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ผู้ให้เช่า
              </label>
              <input
                className="input"
                placeholder="เช่น บจ. ลีสซิ่งกสิกรไทย"
                value={lessor}
                onChange={(e) => setLessor(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  สาขา
                </label>
                <BranchSelect
                  companyId={companyId}
                  value={branchId}
                  onChange={setBranchId}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  หน่วยงานที่ใช้
                </label>
                <DepartmentSelect
                  companyId={companyId}
                  value={departmentId}
                  onChange={setDepartmentId}
                  branchId={branchId}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียดสินทรัพย์
            </label>
            <input
              className="input"
              placeholder={placeholder}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              คำนวณ Present Value (PV)
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                gap: 14,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  อายุสัญญา (เดือน)
                </label>
                <select
                  className="select"
                  value={months}
                  onChange={(e) => setMonths(parseInt(e.target.value))}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option value="12">12 เดือน (1 ปี)</option>
                  <option value="24">24 เดือน (2 ปี)</option>
                  <option value="36">36 เดือน (3 ปี)</option>
                  <option value="48">48 เดือน (4 ปี)</option>
                  <option value="60">60 เดือน (5 ปี)</option>
                  <option value="72">72 เดือน (6 ปี)</option>
                  <option value="120">120 เดือน (10 ปี)</option>
                  <option value="180">180 เดือน (15 ปี)</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ค่าเช่า/เดือน (บาท)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  value={pmt}
                  onChange={(e) => setPmt(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 38,
                    fontSize: 15,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  IBR (%/ปี)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  step="0.1"
                  value={rate}
                  onChange={(e) => setRate(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 38,
                    fontSize: 15,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                />
                <div className="meta" style={{ marginTop: 4, fontSize: 13 }}>
                  อัตราดอกเบี้ยที่ขอกู้ส่วนเพิ่ม
                </div>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันเริ่มสัญญา
                </label>
                <input
                  className="input"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
                display: 'grid',
                gridTemplateColumns: 'repeat(4,1fr)',
                gap: 14,
              }}
            >
              <div>
                <div className="meta">รวมจ่ายตลอดอายุ (Undiscounted)</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(totalPmt, 0)}
                </div>
              </div>
              <div>
                <div className="meta">มูลค่าปัจจุบัน (PV) = ROU + Liab เริ่มต้น</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    marginTop: 4,
                    color: 'var(--brand)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(rouInit, 0)}
                </div>
              </div>
              <div>
                <div className="meta">ดอกเบี้ยรวมตลอดอายุ</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    color: 'var(--info)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(totalInterest, 0)}
                </div>
              </div>
              <div>
                <div className="meta">ค่าเสื่อม ROU/เดือน</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    color: 'var(--warning)',
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(rouInit / months, 0)}
                </div>
              </div>
            </div>
          </div>

          {/* Phase 14 — payment schedule + advanced settings */}
          <div
            style={{
              padding: '14px 16px',
              background: '#fefce8',
              borderRadius: 8,
              border: '1px solid #fde047',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              รูปแบบการจ่ายชำระค่าเช่า
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                marginBottom: 12,
              }}
            >
              {PAYMENT_FREQ_OPTIONS.map((opt) => {
                const active = paymentFrequency === opt.value;
                return (
                  <div
                    key={opt.value}
                    onClick={() => setPaymentFrequency(opt.value)}
                    style={{
                      border: active ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                      background: active ? 'var(--brand-tint, #eef4fc)' : 'white',
                      borderRadius: 8,
                      padding: '10px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: active ? 600 : 500 }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{opt.hint}</div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: requiresMonthOfYear ? '1fr 1fr 1fr' : '1fr 1fr',
                gap: 14,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันที่จ่าย (1-31)
                </label>
                <input
                  className="input tnum"
                  type="number"
                  min={1}
                  max={31}
                  value={paymentDayOfMonth}
                  onChange={(e) =>
                    setPaymentDayOfMonth(Math.max(1, Math.min(31, Number(e.target.value) || 1)))
                  }
                  style={{
                    width: '100%',
                    height: 36,
                    fontSize: 14,
                    textAlign: 'right',
                  }}
                />
                <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                  ถ้า 31 จะใช้วันสุดท้ายของเดือนแทน
                </div>
              </div>
              {requiresMonthOfYear && (
                <div>
                  <label
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    เดือนที่จ่าย (1-12)
                  </label>
                  <select
                    className="select"
                    value={paymentMonthOfYear}
                    onChange={(e) => setPaymentMonthOfYear(Number(e.target.value))}
                    style={{ width: '100%', height: 36, fontSize: 14 }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                      <option key={m} value={m}>
                        เดือน {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ค่าเช่าต่อครั้งจ่าย (บาท)
                </label>
                <div
                  className="tnum"
                  style={{
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--brand)',
                  }}
                >
                  {fmt(pmt, 0)}
                </div>
                <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                  จากช่องค่าเช่าด้านบน
                </div>
              </div>
            </div>
          </div>

          {/* Phase 15 — stepped/escalating rent toggle + editor */}
          <div
            style={{
              padding: '14px 16px',
              background: hasRentSteps ? '#fef3c7' : '#fff',
              borderRadius: 8,
              border: hasRentSteps ? '1px solid #f59e0b' : '1px dashed var(--border)',
              transition: 'all 0.15s',
            }}
          >
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                marginBottom: hasRentSteps ? 12 : 0,
              }}
            >
              <input
                type="checkbox"
                checked={hasRentSteps}
                onChange={(e) => setHasRentSteps(e.target.checked)}
              />
              ค่าเช่าขั้นบันได (Stepped / Escalating rent)
              <span style={{ fontSize: 12, color: '#666', fontWeight: 400, marginLeft: 'auto' }}>
                ตามมาตรฐาน TFRS 16 — ค่าเช่าแต่ละขั้นถูก discount เข้า PV
              </span>
            </label>

            {hasRentSteps && (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr 1fr 1fr 50px',
                    gap: 8,
                    marginBottom: 6,
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#666',
                    paddingLeft: 4,
                  }}
                >
                  <div>ขั้น</div>
                  <div>เดือนเริ่ม</div>
                  <div>เดือนสิ้นสุด</div>
                  <div className="num">ค่าเช่า/ครั้งจ่าย (บาท)</div>
                  <div />
                </div>
                {rentSteps.map((s, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 1fr 1fr 50px',
                      gap: 8,
                      marginBottom: 6,
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: 'var(--brand)',
                        background: 'white',
                        padding: '6px',
                        borderRadius: 6,
                      }}
                    >
                      #{idx + 1}
                    </div>
                    <input
                      className="input tnum"
                      type="number"
                      min={1}
                      max={months}
                      value={s.fromMonth}
                      onChange={(e) => updateStep(idx, { fromMonth: Number(e.target.value) || 1 })}
                      style={{ height: 32, fontSize: 13, textAlign: 'right' }}
                    />
                    <input
                      className="input tnum"
                      type="number"
                      min={s.fromMonth}
                      max={months}
                      value={s.toMonth}
                      onChange={(e) => updateStep(idx, { toMonth: Number(e.target.value) || 1 })}
                      style={{ height: 32, fontSize: 13, textAlign: 'right' }}
                    />
                    <input
                      className="input tnum"
                      type="number"
                      min={0}
                      step={1000}
                      value={s.amount}
                      onChange={(e) => updateStep(idx, { amount: Number(e.target.value) || 0 })}
                      style={{ height: 32, fontSize: 13, textAlign: 'right', fontWeight: 600 }}
                    />
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      disabled={rentSteps.length <= 1}
                      title="ลบขั้น"
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        height: 32,
                        cursor: rentSteps.length <= 1 ? 'not-allowed' : 'pointer',
                        color: rentSteps.length <= 1 ? '#ccc' : '#dc2626',
                      }}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={addStep}
                    style={{
                      background: 'white',
                      border: '1px solid var(--brand)',
                      color: 'var(--brand)',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    + เพิ่มขั้น
                  </button>
                  {!stepValidation.ok && (
                    <span style={{ color: '#b42318', fontSize: 12 }}>⚠ {stepValidation.msg}</span>
                  )}
                  {stepValidation.ok && stepImpact && (
                    <span style={{ color: '#15803d', fontSize: 12, marginLeft: 'auto' }}>
                      ✓ ครอบคลุม {months} เดือนครบ
                    </span>
                  )}
                </div>

                {/* Impact analysis — undiscounted cash + average + per-step contribution */}
                {stepImpact && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      background: 'white',
                      borderRadius: 6,
                      border: '1px solid #fde68a',
                    }}
                  >
                    <div className="section-title" style={{ margin: '0 0 8px', fontSize: 13 }}>
                      ผลกระทบ (ก่อน discount)
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      <div>
                        <div className="meta" style={{ fontSize: 11 }}>
                          รวมค่าเช่าตลอดสัญญา
                        </div>
                        <div
                          className="tnum"
                          style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}
                        >
                          {fmt(stepImpact.totalCash, 0)}
                        </div>
                      </div>
                      <div>
                        <div className="meta" style={{ fontSize: 11 }}>
                          เฉลี่ย/เดือน
                        </div>
                        <div
                          className="tnum"
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            marginTop: 2,
                            color: 'var(--brand)',
                          }}
                        >
                          {fmt(stepImpact.avgPerMonth, 0)}
                        </div>
                      </div>
                    </div>
                    <table className="tbl" style={{ fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th>ขั้น</th>
                          <th>ช่วงเดือน</th>
                          <th className="num">จำนวนเดือน</th>
                          <th className="num">ค่าเช่า/ครั้ง</th>
                          <th className="num">รวมขั้น</th>
                          <th className="num">% ของยอดรวม</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stepImpact.stepRows.map((r, i) => (
                          <tr key={i}>
                            <td>#{i + 1}</td>
                            <td>
                              เดือน {r.fromMonth}-{r.toMonth}
                            </td>
                            <td className="num">{r.cycleCount}</td>
                            <td className="num tnum">{fmt(r.amount, 0)}</td>
                            <td className="num tnum">
                              <b>{fmt(r.subtotal, 0)}</b>
                            </td>
                            <td className="num">
                              {stepImpact.totalCash > 0
                                ? ((r.subtotal / stepImpact.totalCash) * 100).toFixed(1)
                                : 0}
                              %
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="meta" style={{ marginTop: 8, fontSize: 11, lineHeight: 1.5 }}>
                  <b>หมายเหตุ TFRS 16:</b> ระบบจะคำนวณ PV โดย discount ค่าเช่าแต่ละ
                  ขั้นตามจำนวนวันจากวันเริ่มสัญญา ROU ทั้งก้อนเริ่มต้น = PV รวม ค่าเสื่อม +
                  ดอกเบี้ยจะปรับตัวตามตารางอัตโนมัติ ไม่ต้องบันทึก รายการปรับปรุง deferred rent
                </div>
              </>
            )}
          </div>

          {/* Advanced — collapsed by default */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--brand)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                padding: '4px 0',
              }}
            >
              {showAdvanced ? '▾' : '▸'} ตั้งค่าขั้นสูง (day-count + อัตราภาษี)
            </button>
            {showAdvanced && (
              <div
                style={{
                  marginTop: 8,
                  padding: '12px 14px',
                  background: '#fafafa',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: 14,
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    Day-count convention
                  </label>
                  <select
                    className="select"
                    value={dayCountConvention}
                    onChange={(e) => setDayCountConvention(e.target.value as DayCount)}
                    style={{ width: '100%', height: 36, fontSize: 14 }}
                  >
                    {DAY_COUNT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                    ใช้คำนวณดอกเบี้ย + ค่าเสื่อม ROU แบบ day-count
                  </div>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: 13,
                      color: 'var(--text-muted)',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    อัตราภาษี (CIT %)
                  </label>
                  <input
                    className="input tnum"
                    type="number"
                    step="0.5"
                    min={0}
                    max={50}
                    value={taxAnnualRate}
                    onChange={(e) =>
                      setTaxAnnualRate(Math.max(0, Math.min(50, Number(e.target.value) || 0)))
                    }
                    style={{
                      width: '100%',
                      height: 36,
                      fontSize: 14,
                      textAlign: 'right',
                    }}
                  />
                  <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                    ไทยมาตรฐาน 20%
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>
              ตัวอย่างตารางตัดจ่าย (4 งวดแรก จาก {months})
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>งวด</th>
                    <th>เดือน</th>
                    <th className="num">ยอดยกมา</th>
                    <th className="num">ค่าเช่า</th>
                    <th className="num">ดอกเบี้ย</th>
                    <th className="num">เงินต้น</th>
                    <th className="num">ยอดยกไป</th>
                    <th className="num">ค่าเสื่อม ROU</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((s) => (
                    <tr key={s.i}>
                      <td className="num">{s.i}</td>
                      <td>{s.month}</td>
                      <td className="num tnum">{fmt(s.openBal, 0)}</td>
                      <td className="num tnum">{fmt(s.payment, 0)}</td>
                      <td className="num tnum" style={{ color: 'var(--info)' }}>
                        {fmt(s.interest, 0)}
                      </td>
                      <td className="num tnum">{fmt(s.principal, 0)}</td>
                      <td className="num tnum">{fmt(s.closeBal, 0)}</td>
                      <td className="num tnum" style={{ color: 'var(--warning)' }}>
                        {fmt(s.rouDep, 0)}
                      </td>
                    </tr>
                  ))}
                  {months > 4 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="faint"
                        style={{ textAlign: 'center', padding: '8px' }}
                      >
                        ... และอีก {months - 4} งวด จะถูกสร้างเมื่อบันทึก
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="section-title" style={{ marginBottom: 8 }}>
              ใบสำคัญรับรู้สัญญาเช่าครั้งแรก (Day 1)
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>บัญชี</th>
                    <th>ชื่อบัญชี</th>
                    <th className="num">เดบิต</th>
                    <th className="num">เครดิต</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="code">{t.acctROU}</td>
                    <td>สินทรัพย์สิทธิการใช้ — {t.label}</td>
                    <td className="num tnum">{fmt(rouInit, 0)}</td>
                    <td className="num">—</td>
                  </tr>
                  <tr>
                    <td className="code">{t.acctLiab}</td>
                    <td>หนี้สินตามสัญญาเช่า — {t.label}</td>
                    <td className="num">—</td>
                    <td className="num tnum">{fmt(rouInit, 0)}</td>
                  </tr>
                  <tr className="total">
                    <td colSpan={2}>รวม</td>
                    <td className="num tnum">{fmt(rouInit, 0)}</td>
                    <td className="num tnum">{fmt(rouInit, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              <b>การตรวจสอบข้อยกเว้น TFRS 16:</b> สัญญานี้มีอายุ <b>{months} เดือน</b>
              {months <= 12
                ? ' (≤ 12 เดือน) → เข้าข้อยกเว้น Short-term lease — ลงเป็นค่าใช้จ่ายตรงๆ ได้'
                : ' (> 12 เดือน) → ต้องรับรู้เป็น ROU + Lease Liability ตาม TFRS 16'}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn"
              onClick={() => {
                /* TODO(phase-3): wire */
              }}
            >
              ดู Schedule เต็ม ({months} งวด)
            </button>
            <button
              className="btn primary"
              disabled={isSaving || !stepValidation.ok}
              onClick={handleSubmit}
              title={!stepValidation.ok ? stepValidation.msg : ''}
            >
              <Icon name="check" size={14} />{' '}
              {isSaving ? 'กำลังบันทึก...' : 'บันทึกสัญญา & สร้าง JV'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TerminateLeaseModal (Phase 16) — early termination with a live impact
// preview. The user picks a termination date + optional penalty + reason;
// the backend preview endpoint returns the catch-up accrual, the carrying
// amounts as of that date, the gain/loss, and the derecognition JV. Nothing
// posts until the user confirms.
// ============================================================

interface TerminateLeaseModalProps {
  companyId: string | undefined;
  leaseUuid: string;
  lease: Lease;
  onClose: () => void;
  onConfirm: (data: TerminateLease) => void;
  isPosting: boolean;
  postError: string | null;
}

function TerminateLeaseModal({
  companyId,
  leaseUuid,
  lease,
  onClose,
  onConfirm,
  isPosting,
  postError,
}: TerminateLeaseModalProps) {
  // Default termination date = today clamped into the lease term.
  const defaultDate = (() => {
    const today = new Date().toISOString().slice(0, 10);
    if (today < lease.start) return lease.start;
    if (today > lease.end) return lease.end;
    return today;
  })();
  const [terminationDate, setTerminationDate] = useState<string>(defaultDate);
  const [penaltyStr, setPenaltyStr] = useState<string>('0');
  const [reason, setReason] = useState<string>('');

  const penalty = Number(penaltyStr.replace(/,/g, '')) || 0;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(terminationDate);
  const inRange = dateValid && terminationDate >= lease.start && terminationDate <= lease.end;

  const previewQ = useTerminationPreview(
    companyId,
    leaseUuid,
    { terminationDate, penalty },
    inRange,
  );
  const preview = previewQ.data;
  const previewErr = previewQ.error instanceof Error ? previewQ.error.message : null;

  const gainLossNum = preview ? Number(preview.gainLoss) : 0;
  const isGain = preview?.gainLossKind === 'GAIN';
  const isLoss = preview?.gainLossKind === 'LOSS';

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,15,20,0.45)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 100,
    padding: 24,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg,#fef2f2 0%,#fff 100%)',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              ยกเลิกสัญญาเช่าก่อนกำหนด — TFRS 16
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {lease.id} · {lease.asset.slice(0, 50)} · ตัด ROU + หนี้สิน รับรู้กำไร/ขาดทุน
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {/* Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่ยกเลิก *
              </label>
              <input
                type="date"
                className="input"
                value={terminationDate}
                min={lease.start}
                max={lease.end}
                onChange={(e) => setTerminationDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
              <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                ในช่วง {fmtDate(lease.start)} – {fmtDate(lease.end)}
              </div>
            </div>
            <div>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ค่าปรับยกเลิก (จ่ายเงินสด)
              </label>
              <input
                type="number"
                className="input tnum"
                value={penaltyStr}
                min={0}
                step={1000}
                onChange={(e) => setPenaltyStr(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 15,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
              <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                Cr ธนาคาร · รวมในกำไร/ขาดทุน
              </div>
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 13,
                color: 'var(--text-muted)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              เหตุผล / หมายเหตุการยกเลิก
            </label>
            <input
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น คืนรถก่อนกำหนด / เปลี่ยนสัญญาใหม่"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          {!inRange && (
            <div style={{ color: '#b42318', fontSize: 13 }}>
              ⚠ วันที่ยกเลิกต้องอยู่ในช่วงอายุสัญญา
            </div>
          )}
          {previewErr && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: '#b42318',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {previewErr}
            </div>
          )}

          {previewQ.isFetching && !preview && (
            <div className="meta" style={{ padding: 16, textAlign: 'center' }}>
              กำลังคำนวณผลกระทบ…
            </div>
          )}

          {preview && (
            <>
              {/* Impact summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <div
                  style={{
                    padding: '12px 14px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    หนี้สินคงเหลือ ณ วันยกเลิก
                  </div>
                  <div className="tnum" style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>
                    {fmt(Number(preview.remainingLiability), 0)}
                  </div>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    ROU คงเหลือ (NBV)
                  </div>
                  <div className="tnum" style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>
                    {fmt(Number(preview.rouNbv), 0)}
                  </div>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: `1px solid ${isGain ? '#15803d' : isLoss ? '#b42318' : 'var(--border)'}`,
                    background: isGain
                      ? 'rgba(21,128,61,0.06)'
                      : isLoss
                        ? 'rgba(180,35,24,0.06)'
                        : '#fafafa',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    {isGain ? 'กำไรจากการยกเลิก' : isLoss ? 'ขาดทุนจากการยกเลิก' : 'กำไร/ขาดทุน'}
                  </div>
                  <div
                    className="tnum"
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      marginTop: 4,
                      color: isGain ? '#15803d' : isLoss ? '#b42318' : 'inherit',
                    }}
                  >
                    {gainLossNum >= 0 ? '+' : ''}
                    {fmt(gainLossNum, 0)}
                  </div>
                  <div className="meta" style={{ fontSize: 11, marginTop: 2 }}>
                    {isGain ? 'Cr 40130013' : isLoss ? 'Dr 40130013' : '—'}
                  </div>
                </div>
              </div>

              {/* Catch-up note */}
              {preview.catchUp.rowCount > 0 && (
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <b>Catch-up ก่อนยกเลิก:</b> ดอกเบี้ย {fmt(Number(preview.catchUp.interest), 0)} ·
                  ค่าเสื่อม ROU {fmt(Number(preview.catchUp.depreciation), 0)} · ค่าเช่าจ่าย{' '}
                  {fmt(Number(preview.catchUp.payment), 0)} ({preview.catchUp.rowCount} งวด)
                  <div className="meta" style={{ fontSize: 11, marginTop: 2 }}>
                    ระบบบันทึกดอกเบี้ย + ค่าเสื่อมค้างถึงวันยกเลิกอัตโนมัติก่อน derecognition
                  </div>
                </div>
              )}

              {/* Derecognition JV */}
              <div>
                <div className="section-title" style={{ marginBottom: 8 }}>
                  ใบสำคัญตัดรายการ (Derecognition JV)
                </div>
                <div
                  style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
                >
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>บัญชี</th>
                        <th>คำอธิบาย</th>
                        <th className="num">เดบิต</th>
                        <th className="num">เครดิต</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.derecognitionLines.map((ln, i) => (
                        <tr key={i}>
                          <td className="code">{ln.accountCode}</td>
                          <td>{ln.description}</td>
                          <td className="num tnum">
                            {Number(ln.debit) > 0 ? fmt(Number(ln.debit), 2) : '—'}
                          </td>
                          <td className="num tnum">
                            {Number(ln.credit) > 0 ? fmt(Number(ln.credit), 2) : '—'}
                          </td>
                        </tr>
                      ))}
                      <tr className="total">
                        <td colSpan={2}>รวม</td>
                        <td className="num tnum">
                          {fmt(
                            preview.derecognitionLines.reduce((s, l) => s + Number(l.debit), 0),
                            2,
                          )}
                        </td>
                        <td className="num tnum">
                          {fmt(
                            preview.derecognitionLines.reduce((s, l) => s + Number(l.credit), 0),
                            2,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {postError && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: '#b42318',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {postError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <div className="meta" style={{ fontSize: 12 }}>
            ยืนยันแล้วระบบจะ post JV และเปลี่ยนสถานะเป็น “ยกเลิก”
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={onClose}>
              ปิด
            </button>
            <button
              className="btn primary"
              disabled={!inRange || !preview || isPosting}
              onClick={() =>
                onConfirm({
                  terminationDate,
                  penalty,
                  ...(reason.trim() ? { reason: reason.trim() } : {}),
                })
              }
              style={{ background: '#b42318', borderColor: '#b42318' }}
            >
              <Icon name="check" size={14} /> {isPosting ? 'กำลังบันทึก...' : 'ยืนยันยกเลิกสัญญา'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ModifyLeaseModal (Phase 17) — mid-term rent change (TFRS 16 remeasurement)
// with a live impact preview. The user picks an effective date, a revised
// IBR, and the new rent (stepped) from the effective month through lease end.
// The backend remeasures the liability and adjusts the ROU asset; the modal
// shows old→new liability, the ROU adjustment, and the remeasurement JV
// before the user confirms.
// ============================================================

interface ModifyLeaseModalProps {
  companyId: string | undefined;
  leaseUuid: string;
  lease: Lease;
  onClose: () => void;
  onConfirm: (data: ModifyLease) => void;
  isPosting: boolean;
  postError: string | null;
}

interface ModStep {
  fromMonth: number;
  toMonth: number;
  amount: number;
}

function ModifyLeaseModal({
  companyId,
  leaseUuid,
  lease,
  onClose,
  onConfirm,
  isPosting,
  postError,
}: ModifyLeaseModalProps) {
  // Effective date defaults to the 1st of next month, clamped into the term
  // (must be strictly after the lease start).
  const defaultEff = (() => {
    const d = new Date();
    const firstNext = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))
      .toISOString()
      .slice(0, 10);
    if (firstNext < lease.start) return lease.start;
    if (firstNext > lease.end) return lease.end;
    return firstNext;
  })();
  const [effectiveDate, setEffectiveDate] = useState<string>(defaultEff);
  const [rateStr, setRateStr] = useState<string>((lease.rate * 100).toFixed(2));
  const [reason, setReason] = useState<string>('');

  // Remaining months from the effective month through the lease-end month
  // (1-indexed, inclusive) — the step ranges must cover [1, remainingMonths].
  const remainingMonths = (() => {
    const e = new Date(effectiveDate);
    const end = new Date(lease.end);
    if (Number.isNaN(e.getTime()) || Number.isNaN(end.getTime())) return 0;
    return (
      (end.getUTCFullYear() - e.getUTCFullYear()) * 12 + (end.getUTCMonth() - e.getUTCMonth()) + 1
    );
  })();

  // Seed one step covering the whole remaining term at the current rent.
  const [steps, setSteps] = useState<ModStep[]>([
    { fromMonth: 1, toMonth: Math.max(1, remainingMonths), amount: lease.pmtMonthly },
  ]);

  const rate = Number(rateStr) || 0;
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(effectiveDate);
  const inRange = dateValid && effectiveDate > lease.start && effectiveDate <= lease.end;

  // Client-side step validation mirroring the backend builder.
  const stepValidation = (() => {
    if (steps.length === 0) return { ok: false, msg: 'ต้องมีอย่างน้อย 1 ขั้น' };
    const sorted = [...steps].sort((a, b) => a.fromMonth - b.fromMonth);
    if (sorted[0]!.fromMonth !== 1)
      return { ok: false, msg: 'ขั้นแรกต้องเริ่มเดือน 1 (นับจากวันมีผล)' };
    for (let i = 0; i < sorted.length; i++) {
      const cur = sorted[i]!;
      if (cur.toMonth < cur.fromMonth)
        return { ok: false, msg: `ขั้น #${i + 1}: toMonth < fromMonth` };
      if (cur.amount <= 0) return { ok: false, msg: `ขั้น #${i + 1}: ค่าเช่าต้องมากกว่า 0` };
      if (i > 0 && cur.fromMonth !== sorted[i - 1]!.toMonth + 1) {
        return { ok: false, msg: `ขั้น #${i + 1}: มีช่องว่าง/ซ้อนกับขั้นก่อนหน้า` };
      }
    }
    if (sorted[sorted.length - 1]!.toMonth !== remainingMonths) {
      return {
        ok: false,
        msg: `ขั้นสุดท้ายต้องสิ้นสุดเดือน ${remainingMonths} (เดือนที่เหลือถึงสิ้นสุดสัญญา)`,
      };
    }
    return { ok: true, msg: '' };
  })();

  const addStep = () =>
    setSteps((prev) => {
      const last = prev[prev.length - 1];
      const from = last ? last.toMonth + 1 : 1;
      return [
        ...prev,
        { fromMonth: from, toMonth: Math.max(from, remainingMonths), amount: lease.pmtMonthly },
      ];
    });
  const removeStep = (i: number) => setSteps((prev) => prev.filter((_, idx) => idx !== i));
  const updateStep = (i: number, patch: Partial<ModStep>) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const canPreview = inRange && stepValidation.ok && rate >= 0;
  const previewQ = useModificationPreview(
    companyId,
    leaseUuid,
    { effectiveDate, revisedDiscountRate: rate, newRentSteps: steps },
    canPreview,
  );
  const preview = previewQ.data;
  const previewErr = previewQ.error instanceof Error ? previewQ.error.message : null;

  const adj = preview ? Number(preview.liabilityAdjustment) : 0;
  const isDecrease = preview?.adjustmentKind === 'DECREASE';
  const isIncrease = preview?.adjustmentKind === 'INCREASE';

  const overlay: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,15,20,0.45)',
    display: 'grid',
    placeItems: 'center',
    zIndex: 100,
    padding: 24,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 880,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg,#eff6ff 0%,#fff 100%)',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              เปลี่ยนแปลงค่าเช่าระหว่างสัญญา — TFRS 16 Remeasurement
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {lease.id} · {lease.asset.slice(0, 50)} · remeasure หนี้สิน + ปรับ ROU (ไม่ลง P&L)
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {/* Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันที่มีผล *
              </label>
              <input
                type="date"
                className="input"
                value={effectiveDate}
                min={lease.start}
                max={lease.end}
                onChange={(e) => setEffectiveDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
              <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                หลัง {fmtDate(lease.start)} ถึง {fmtDate(lease.end)} · เหลือ {remainingMonths} เดือน
              </div>
            </div>
            <div>
              <label
                style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Revised IBR (%/ปี) *
              </label>
              <input
                type="number"
                step="0.1"
                className="input tnum"
                value={rateStr}
                onChange={(e) => setRateStr(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 15,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
              <div className="meta" style={{ marginTop: 4, fontSize: 12 }}>
                อัตราคิดลดใหม่ ณ วันมีผล (default = อัตราเดิม)
              </div>
            </div>
          </div>

          {/* New rent step editor */}
          <div
            style={{
              padding: '14px 16px',
              background: '#fefce8',
              borderRadius: 8,
              border: '1px solid #fde047',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              ค่าเช่าใหม่ (นับเดือนจากวันมีผล ถึงสิ้นสุดสัญญา)
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr 1fr 1fr 50px',
                gap: 8,
                marginBottom: 6,
                fontSize: 12,
                fontWeight: 500,
                color: '#666',
                paddingLeft: 4,
              }}
            >
              <div>ขั้น</div>
              <div>เดือนเริ่ม</div>
              <div>เดือนสิ้นสุด</div>
              <div className="num">ค่าเช่า/งวด (บาท)</div>
              <div />
            </div>
            {steps.map((s, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr 1fr 1fr 50px',
                  gap: 8,
                  marginBottom: 6,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'var(--brand)',
                    background: 'white',
                    padding: 6,
                    borderRadius: 6,
                  }}
                >
                  #{idx + 1}
                </div>
                <input
                  className="input tnum"
                  type="number"
                  min={1}
                  max={remainingMonths}
                  value={s.fromMonth}
                  onChange={(e) => updateStep(idx, { fromMonth: Number(e.target.value) || 1 })}
                  style={{ height: 32, fontSize: 13, textAlign: 'right' }}
                />
                <input
                  className="input tnum"
                  type="number"
                  min={s.fromMonth}
                  max={remainingMonths}
                  value={s.toMonth}
                  onChange={(e) => updateStep(idx, { toMonth: Number(e.target.value) || 1 })}
                  style={{ height: 32, fontSize: 13, textAlign: 'right' }}
                />
                <input
                  className="input tnum"
                  type="number"
                  min={0}
                  step={1000}
                  value={s.amount}
                  onChange={(e) => updateStep(idx, { amount: Number(e.target.value) || 0 })}
                  style={{ height: 32, fontSize: 13, textAlign: 'right', fontWeight: 600 }}
                />
                <button
                  type="button"
                  onClick={() => removeStep(idx)}
                  disabled={steps.length <= 1}
                  title="ลบขั้น"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    height: 32,
                    cursor: steps.length <= 1 ? 'not-allowed' : 'pointer',
                    color: steps.length <= 1 ? '#ccc' : '#dc2626',
                  }}
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                onClick={addStep}
                style={{
                  background: 'white',
                  border: '1px solid var(--brand)',
                  color: 'var(--brand)',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                + เพิ่มขั้น
              </button>
              {!stepValidation.ok && (
                <span style={{ color: '#b42318', fontSize: 12 }}>⚠ {stepValidation.msg}</span>
              )}
              {stepValidation.ok && (
                <span style={{ color: '#15803d', fontSize: 12, marginLeft: 'auto' }}>
                  ✓ ครอบคลุม {remainingMonths} เดือนครบ
                </span>
              )}
            </div>
          </div>

          <div>
            <label
              style={{
                fontSize: 13,
                color: 'var(--text-muted)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              เหตุผล / หมายเหตุ
            </label>
            <input
              className="input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ต่อรองลดค่าเช่า COVID / เจรจาใหม่"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          {!inRange && (
            <div style={{ color: '#b42318', fontSize: 13 }}>
              ⚠ วันที่มีผลต้องอยู่หลังวันเริ่มสัญญาและไม่เกินวันสิ้นสุด
            </div>
          )}
          {previewErr && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: '#b42318',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {previewErr}
            </div>
          )}
          {previewQ.isFetching && !preview && (
            <div className="meta" style={{ padding: 16, textAlign: 'center' }}>
              กำลังคำนวณผลกระทบ…
            </div>
          )}

          {preview && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <div
                  style={{
                    padding: '12px 14px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    หนี้สินเดิม → ใหม่
                  </div>
                  <div className="tnum" style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                    {fmt(Number(preview.oldLiability), 0)} → {fmt(Number(preview.newLiability), 0)}
                  </div>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: `1px solid ${isDecrease ? '#15803d' : isIncrease ? '#1a56a6' : 'var(--border)'}`,
                    background: isDecrease
                      ? 'rgba(21,128,61,0.06)'
                      : isIncrease
                        ? 'rgba(26,86,166,0.06)'
                        : '#fafafa',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    ปรับปรุงหนี้สิน {isDecrease ? '(ลดลง)' : isIncrease ? '(เพิ่มขึ้น)' : ''}
                  </div>
                  <div
                    className="tnum"
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      marginTop: 4,
                      color: isDecrease ? '#15803d' : isIncrease ? '#1a56a6' : 'inherit',
                    }}
                  >
                    {adj >= 0 ? '+' : ''}
                    {fmt(adj, 0)}
                  </div>
                  <div className="meta" style={{ fontSize: 11, marginTop: 2 }}>
                    ปรับ ROU เท่ากัน · ไม่ลง P&L
                  </div>
                </div>
                <div
                  style={{
                    padding: '12px 14px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}
                >
                  <div className="meta" style={{ fontSize: 11 }}>
                    ROU คงเหลือ เดิม → ใหม่
                  </div>
                  <div className="tnum" style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                    {fmt(Number(preview.oldRouNbv), 0)} → {fmt(Number(preview.newRouCarrying), 0)}
                  </div>
                  {Number(preview.plGain) > 0 && (
                    <div style={{ fontSize: 11, marginTop: 2, color: '#15803d' }}>
                      + กำไร {fmt(Number(preview.plGain), 0)} (ROU floored)
                    </div>
                  )}
                </div>
              </div>

              {preview.catchUp.rowCount > 0 && (
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <b>Catch-up ก่อนมีผล:</b> ดอกเบี้ย {fmt(Number(preview.catchUp.interest), 0)} ·
                  ค่าเสื่อม {fmt(Number(preview.catchUp.depreciation), 0)} · ค่าเช่า{' '}
                  {fmt(Number(preview.catchUp.payment), 0)} ({preview.catchUp.rowCount} งวด
                  ตามค่าเช่าเดิมถึงวันก่อนมีผล)
                </div>
              )}

              <div>
                <div className="section-title" style={{ marginBottom: 8 }}>
                  ใบสำคัญปรับปรุง (Remeasurement JV) · IBR ใหม่{' '}
                  {Number(preview.revisedDiscountRate).toFixed(2)}%
                </div>
                <div
                  style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}
                >
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>บัญชี</th>
                        <th>คำอธิบาย</th>
                        <th className="num">เดบิต</th>
                        <th className="num">เครดิต</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.remeasureLines.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="faint"
                            style={{ textAlign: 'center', padding: 10 }}
                          >
                            ไม่มีผลต่างให้ปรับปรุง (หนี้สินใหม่ = เดิม)
                          </td>
                        </tr>
                      ) : (
                        preview.remeasureLines.map((ln, i) => (
                          <tr key={i}>
                            <td className="code">{ln.accountCode}</td>
                            <td>{ln.description}</td>
                            <td className="num tnum">
                              {Number(ln.debit) > 0 ? fmt(Number(ln.debit), 2) : '—'}
                            </td>
                            <td className="num tnum">
                              {Number(ln.credit) > 0 ? fmt(Number(ln.credit), 2) : '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="meta" style={{ fontSize: 12, marginTop: 6 }}>
                  ค่าเช่าใหม่ {preview.newSchedule.paymentCount} งวด · รวม{' '}
                  {fmt(Number(preview.newSchedule.totalPayments), 0)} บาท ·
                  ระบบจะสร้างตารางตัดจ่ายใหม่ตั้งแต่วันมีผล
                </div>
              </div>
            </>
          )}

          {postError && (
            <div
              style={{
                background: 'rgba(180,35,24,0.08)',
                color: '#b42318',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {postError}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <div className="meta" style={{ fontSize: 12 }}>
            ยืนยันแล้วระบบจะ post JV ปรับปรุง + สร้างตารางใหม่ตั้งแต่วันมีผล
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost" onClick={onClose}>
              ปิด
            </button>
            <button
              className="btn primary"
              disabled={!canPreview || !preview || isPosting}
              onClick={() =>
                onConfirm({
                  effectiveDate,
                  revisedDiscountRate: rate,
                  newRentSteps: steps,
                  ...(reason.trim() ? { reason: reason.trim() } : {}),
                })
              }
            >
              <Icon name="check" size={14} /> {isPosting ? 'กำลังบันทึก...' : 'ยืนยันปรับปรุงสัญญา'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
